import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { useVocabulary } from './contexts/VocabularyContext';
import './css/Vocabulary.css';
import './css/VocabularyQuiz.css';

const CIRCLED = ['①', '②', '③', '④', '⑤', '⑥', '⑦', '⑧', '⑨', '⑩'];

// 캡처 배율. 2배로 떠야 인쇄해도 글씨가 뭉개지지 않는다
const CAPTURE_SCALE = 2;

// A4 실측 (96dpi 기준 210×297mm). CSS의 .quiz-paper-page 와 반드시 같아야 한다
const A4_W = 794;
const A4_H = 1123;
const PAD_X = 52;
const PAD_Y = 48;
const COL_GAP = 28;
// 페이지 번호 자리. .quiz-paper-footer 가 bottom:18px + 글자높이라 넉넉히 잡는다
const FOOTER_H = 36;
const CONTENT_W = A4_W - PAD_X * 2;                    // 690
const COL_W = Math.floor((CONTENT_W - COL_GAP) / 2);   // 331
const CONTENT_H = A4_H - PAD_Y * 2 - FOOTER_H;         // 1005

/**
 * 측정한 높이로 문항을 2단 페이지에 채운다.
 *
 * 머리글은 페이지 전체 폭을 차지하므로 그 아래 두 열이 **모두** 짧아진다.
 * 왼쪽 열에서만 머리글 높이를 빼면 오른쪽 열이 페이지 밖으로 흘러
 * 페이지 번호를 덮는다.
 *
 * @param {object[]} items  {key} 를 가진 항목들
 * @param {Map<string, number>} heights key → 실측 높이(px)
 * @param {number} firstPageOffset 첫 페이지에서 머리글이 차지한 높이
 * @returns {Array<[object[], object[]]>} 페이지별 [왼쪽 열, 오른쪽 열]
 */
const paginate = (items, heights, firstPageOffset) => {
  const pages = [];
  let idx = 0;
  let isFirstPage = true;

  while (idx < items.length) {
    const limit = CONTENT_H - (isFirstPage ? firstPageOffset : 0);
    const cols = [[], []];

    for (let c = 0; c < 2 && idx < items.length; c++) {
      let used = 0;
      while (idx < items.length) {
        const h = heights.get(items[idx].key) || 0;
        // used > 0 조건이 있어야 한 문항이 한 열보다 길어도 무한 루프가 되지 않는다
        if (used > 0 && used + h > limit) break;
        cols[c].push(items[idx]);
        used += h;
        idx++;
      }
    }

    pages.push(cols);
    isFirstPage = false;
  }

  return pages;
};

/**
 * 시험지에 찍을 날짜. 로컬 기준 YYYY-MM-DD.
 *
 * toLocaleDateString은 쓰지 않는다 — 이 앱의 언어 코드(kr/ch/jp)는
 * BCP-47이 아니라서 로캘로 넘기면 엉뚱하게 동작한다.
 */
const formatDate = (value) => {
  if (!value) return '';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

/**
 * 시험지 미리보기 + PDF 저장.
 *
 * 2단 A4 페이지로 조판한다. 먼저 열 폭에서 문항 높이를 실측한 뒤
 * 페이지에 채우고, PDF는 페이지마다 따로 캡처한다.
 * 전체를 한 번에 캡처하면 문항이 많을 때 캔버스 높이 한계에 걸린다.
 */
const VocabularyQuizPaper = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { quizId } = useParams();
  const { quizApi } = useVocabulary();

  const [paper, setPaper] = useState(null);
  const [withAnswers, setWithAnswers] = useState(false);
  const [pages, setPages] = useState(null);   // null이면 아직 측정 전
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const measureRef = useRef(null);
  const pageRefs = useRef([]);

  const load = useCallback(async (includeAnswers) => {
    const data = await quizApi.getQuizPaper(quizId, includeAnswers);
    if (!data.success) {
      // 응시 중에는 정답지를 뽑을 수 없다 — 문제지만 있는 상태로 되돌린다
      if (includeAnswers) {
        toast.error(t('vocabulary.quiz.answerKeyBlocked'));
        setWithAnswers(false);
        setLoading(false);
        return;
      }
      toast.error(t('vocabulary.quiz.notFound'));
      navigate('/vocabulary/quiz');
      return;
    }
    setPaper(data);
    setPages(null);        // 내용이 바뀌었으니 다시 조판한다
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quizId]);

  useEffect(() => { load(withAnswers); }, [withAnswers, load]);

  const questionItems = useMemo(
    () => (paper?.questions || []).map(q => ({ key: `q${q.question_id}`, q })),
    [paper]
  );

  const answerItems = useMemo(
    () => (paper?.include_answers ? (paper.questions || []).map(q => ({ key: `a${q.question_id}`, q })) : []),
    [paper]
  );

  // 열 폭에서 실측한 높이로 페이지를 만든다
  useEffect(() => {
    if (!paper || pages !== null) return;
    const node = measureRef.current;
    if (!node) return;

    /*
     * 바깥 여백까지 더해서 잰다.
     * 문항에는 margin-bottom이 있는데 이 여백은 래퍼 밖으로 collapse되어
     * getBoundingClientRect().height에 잡히지 않는다. 그대로 쓰면 열마다
     * 여백 합계만큼 높이를 적게 보고 페이지 번호 위로 넘쳐 흐른다.
     */
    const heights = new Map();
    node.querySelectorAll('[data-mkey]').forEach(el => {
      const child = el.firstElementChild;
      let margin = 0;
      if (child) {
        const style = window.getComputedStyle(child);
        margin = (parseFloat(style.marginTop) || 0) + (parseFloat(style.marginBottom) || 0);
      }
      heights.set(el.dataset.mkey, el.getBoundingClientRect().height + margin);
    });
    const headH = node.querySelector('[data-mhead]')?.getBoundingClientRect().height || 0;
    const keyHeadH = node.querySelector('[data-mkeyhead]')?.getBoundingClientRect().height || 0;

    const built = paginate(questionItems, heights, headH)
      .map((cols, i) => ({ cols, header: i === 0 ? 'main' : null, kind: 'q' }));

    if (answerItems.length > 0) {
      paginate(answerItems, heights, keyHeadH)
        .forEach((cols, i) => built.push({ cols, header: i === 0 ? 'key' : null, kind: 'a' }));
    }

    pageRefs.current = [];
    setPages(built);
  }, [paper, pages, questionItems, answerItems]);

  const handleExport = async () => {
    if (!pages || pages.length === 0 || exporting) return;
    setExporting(true);
    try {
      const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();

      for (let i = 0; i < pages.length; i++) {
        const node = pageRefs.current[i];
        if (!node) continue;

        // 페이지 단위로 캡처한다 (전체를 한 번에 뜨면 캔버스 높이 한계에 걸린다)
        // eslint-disable-next-line no-await-in-loop
        const canvas = await html2canvas(node, {
          scale: CAPTURE_SCALE,
          backgroundColor: '#ffffff',
          useCORS: true,
          logging: false,
          width: A4_W,
          height: A4_H,
          windowWidth: A4_W,
        });

        if (i > 0) pdf.addPage();
        pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, pw, ph);
      }

      const base = (paper.quiz.title || `quiz_${quizId}`).replace(/[^\w가-힣-]+/g, '_');
      const suffix = withAnswers ? `_${t('vocabulary.quiz.answerKeyFileSuffix')}` : '';
      pdf.save(`${base}${suffix}.pdf`);
    } catch (e) {
      console.error('Quiz paper PDF export failed:', e);
      toast.error(t('vocabulary.quiz.pdfFailed'));
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return <div className="vocab-container"><p className="vocab-empty">{t('vocabulary.loading')}</p></div>;
  }
  if (!paper) {
    return (
      <div className="vocab-container">
        <p className="vocab-empty">{t('vocabulary.quiz.notFound')}</p>
        <button className="vocab-btn" onClick={() => navigate('/vocabulary/quiz')}>
          {t('vocabulary.quiz.backToList')}
        </button>
      </div>
    );
  }

  const { quiz, questions } = paper;
  const paperTitle = quiz.title || t('vocabulary.quiz.untitled');

  const renderHeader = () => (
    <header className="quiz-paper-head">
      <h1 className="quiz-paper-title">{paperTitle}</h1>
      <div className="quiz-paper-fields">
        {/* 이름·날짜는 시스템이 아는 값이라 채워서 낸다. 점수만 손으로 적는다 */}
        <span>
          {t('vocabulary.quiz.paperName')}
          <b className="quiz-paper-filled">{quiz.examinee_name || ''}</b>
        </span>
        <span>
          {t('vocabulary.quiz.paperDate')}
          <b className="quiz-paper-filled">{formatDate(quiz.created_at)}</b>
        </span>
        <span className="quiz-paper-score">
          {t('vocabulary.quiz.paperScore')} <b>/ {questions.length}</b>
        </span>
      </div>
      <p className="quiz-paper-info">
        {t('vocabulary.quiz.paperMeta', { count: questions.length })}
        {quiz.source_dates.length > 0 && ` · ${quiz.source_dates.join(', ')}`}
      </p>
    </header>
  );

  const renderKeyHeader = () => (
    <h2 className="quiz-paper-key-head">{t('vocabulary.quiz.answerKey')}</h2>
  );

  const renderQuestion = (q) => (
    <div className="quiz-paper-q">
      <p className="quiz-paper-prompt">
        <span className="quiz-paper-num">{q.order_index + 1}.</span>
        <span className="quiz-paper-text">{q.prompt}</span>
        {q.part_of_speech && <em className="quiz-paper-pos">{q.part_of_speech}</em>}
      </p>

      {q.format === 'multiple_choice' && q.choices ? (
        <ul className="quiz-paper-choices">
          {q.choices.map((choice, i) => (
            <li key={i}>
              <span className="quiz-paper-circle">{CIRCLED[i] || i + 1}</span>
              {choice}
            </li>
          ))}
        </ul>
      ) : (
        <div className="quiz-paper-blank" />
      )}
    </div>
  );

  const renderAnswer = (q) => (
    <div className="quiz-paper-key-item">
      <span className="quiz-paper-key-num">{q.order_index + 1}</span>
      <span className="quiz-paper-key-answer">
        {q.format === 'multiple_choice'
          ? `${CIRCLED[q.answer_index] || q.answer_index + 1} ${q.correct_answer}`
          : q.correct_answer}
      </span>
    </div>
  );

  return (
    <div className="vocab-container">
      <div className="vocab-header">
        <h2>{t('vocabulary.quiz.paperTitle')}</h2>
        <button className="vocab-btn" onClick={() => navigate('/vocabulary/quiz')}>
          {t('vocabulary.quiz.backToList')}
        </button>
      </div>

      <div className="paper-toolbar">
        <label className="vocab-checkbox">
          <input
            type="checkbox"
            checked={withAnswers}
            onChange={(e) => setWithAnswers(e.target.checked)}
          />
          {t('vocabulary.quiz.includeAnswerKey')}
        </label>
        <button
          className="vocab-btn vocab-btn-primary"
          onClick={handleExport}
          disabled={exporting || !pages}
        >
          {exporting ? t('vocabulary.loading') : t('vocabulary.quiz.downloadPdf')}
        </button>
        {pages && (
          <span className="paper-pagecount">
            {t('vocabulary.quiz.paperPages', { count: pages.length })}
          </span>
        )}
      </div>
      <p className="quiz-hint">{t('vocabulary.quiz.paperHint')}</p>

      {/* 1단계: 열 폭에서 높이를 실측한다 (화면 밖) */}
      {pages === null && (
        <div className="quiz-paper-offscreen" ref={measureRef} aria-hidden="true">
          <div style={{ width: CONTENT_W }} data-mhead>{renderHeader()}</div>
          <div style={{ width: CONTENT_W }} data-mkeyhead>{renderKeyHeader()}</div>
          <div style={{ width: COL_W }}>
            {questionItems.map(it => (
              <div key={it.key} data-mkey={it.key}>{renderQuestion(it.q)}</div>
            ))}
            {answerItems.map(it => (
              <div key={it.key} data-mkey={it.key}>{renderAnswer(it.q)}</div>
            ))}
          </div>
        </div>
      )}

      {/* 2단계: 페이지별로 조판. 좁은 화면에서는 가로 스크롤된다 */}
      <div className="paper-preview">
        {pages === null && <p className="vocab-empty">{t('vocabulary.loading')}</p>}
        {pages && pages.map((page, i) => (
          <div
            className="quiz-paper-page"
            key={i}
            ref={(el) => { pageRefs.current[i] = el; }}
          >
            {page.header === 'main' && renderHeader()}
            {page.header === 'key' && renderKeyHeader()}

            <div className="quiz-paper-cols">
              {page.cols.map((colItems, c) => (
                <div className="quiz-paper-col" key={c}>
                  {colItems.map(it => (
                    <React.Fragment key={it.key}>
                      {page.kind === 'q' ? renderQuestion(it.q) : renderAnswer(it.q)}
                    </React.Fragment>
                  ))}
                </div>
              ))}
            </div>

            <div className="quiz-paper-footer">{i + 1} / {pages.length}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default VocabularyQuizPaper;
